import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {useReportGenerationToasts} from '../../../shared/context/ReportGenerationToastContext'
import {buildPdfFileName, downloadBlobAsFile} from '../../../shared/utils/fileDownload'
import {
  downloadPcgReportGenerationJobFile,
  getActivePcgReportGenerationJob,
  getPcgReportGenerationJob,
} from '../api/report-jobs'
import {buildPcgReportProgressToastMessage} from '../components/PcgReportProgressToastMessage'
import type {PcgReportGenerationJob, PcgReportGenerationJobAction} from '../models'

type TrackedPcgReportJob = {
  jobId: string
  reportId: string
  action: PcgReportGenerationJobAction
  processId: string
  fallbackFileName: string
}

type TrackPcgReportJobOptions = {
  processId?: string
  fallbackFileName?: string
}

interface PcgReportGenerationTrackerContextValue {
  trackPcgReportJob: (job: PcgReportGenerationJob, options?: TrackPcgReportJobOptions) => string
  ensureTrackingForReport: (reportId: string, options?: TrackPcgReportJobOptions) => Promise<void>
  isReportJobActive: (reportId?: string) => boolean
  getReportJobAction: (reportId?: string) => PcgReportGenerationJobAction | null
}

const PcgReportGenerationTrackerContext =
  createContext<PcgReportGenerationTrackerContextValue | undefined>(undefined)

const POLL_INTERVAL_MS = 2000
const PREVIEW_TOAST_EXPIRY_MS = 2 * 60 * 60 * 1000
const PdfPreviewModal = React.lazy(() => import('../../../shared/components/PdfPreviewModal'))

const removeTrackedJob = (items: Record<string, TrackedPcgReportJob>, jobId: string) => {
  if (!items[jobId]) {
    return items
  }

  const next = {...items}
  delete next[jobId]
  return next
}

export const PcgReportGenerationTrackerProvider: React.FC<PropsWithChildren<{}>> = ({children}) => {
  const {startProcess, updateProcess, completeProcess, failProcess, dismissProcess} = useReportGenerationToasts()
  const [trackedJobs, setTrackedJobs] = useState<Record<string, TrackedPcgReportJob>>({})
  const trackedJobsRef = useRef<Record<string, TrackedPcgReportJob>>({})
  const pollInFlightRef = useRef<Set<string>>(new Set())
  const ensureInFlightReportIdsRef = useRef<Set<string>>(new Set())
  const [previewBlob, setPreviewBlob] = useState<Blob | undefined>()
  const [previewFileName, setPreviewFileName] = useState('pcg-report.pdf')
  const [showPreviewModal, setShowPreviewModal] = useState(false)

  useEffect(() => {
    trackedJobsRef.current = trackedJobs
  }, [trackedJobs])

  const closePreviewModal = useCallback(() => {
    setShowPreviewModal(false)
    setPreviewBlob(undefined)
  }, [])

  const openPreviewFromJob = useCallback(
    async (jobId: string, fileName: string, processId: string) => {
      try {
        const blob = await downloadPcgReportGenerationJobFile(jobId)
        setPreviewBlob(blob)
        setPreviewFileName(buildPdfFileName(fileName))
        setShowPreviewModal(true)
        dismissProcess(processId)
      } catch (error) {
        console.error('Failed to open completed PCG preview job', error)
        failProcess(processId, {
          title: 'PCG Report',
          message: 'Preview is ready, but opening the PDF failed. Please generate again.',
        })
      }
    },
    [dismissProcess, failProcess]
  )

  const trackPcgReportJob = useCallback(
    (job: PcgReportGenerationJob, options?: TrackPcgReportJobOptions) => {
      const existing = trackedJobsRef.current[job.jobId]
      if (existing) {
        if (options?.processId && options.processId !== existing.processId) {
          dismissProcess(options.processId)
        }
        return existing.processId
      }

      const reportId = String(job.reportId)
      const processId = options?.processId ?? startProcess({
        title: 'PCG Report',
        message: buildPcgReportProgressToastMessage(job.progressPercent ?? 0, job.stage),
      })
      const fallbackFileName =
        options?.fallbackFileName?.trim() ||
        job.fileName?.trim() ||
        `pcg-report-${reportId}.pdf`
      const action: PcgReportGenerationJobAction = job.action === 'preview' ? 'preview' : 'generate'

      setTrackedJobs((current) => ({
        ...current,
        [job.jobId]: {
          jobId: job.jobId,
          reportId,
          action,
          processId,
          fallbackFileName,
        },
      }))

      return processId
    },
    [dismissProcess, startProcess]
  )

  const handleCompletedJob = useCallback(
    async (tracked: TrackedPcgReportJob, job: PcgReportGenerationJob) => {
      const resolvedFileName = job.fileName?.trim() || tracked.fallbackFileName || 'pcg-report.pdf'

      if (tracked.action === 'preview') {
        completeProcess(tracked.processId, {
          title: 'PCG Report',
          message: 'Preview is ready.',
          autoDismissMs: PREVIEW_TOAST_EXPIRY_MS,
          action: {
            label: 'Open Preview',
            onClick: () => {
              void openPreviewFromJob(job.jobId, resolvedFileName, tracked.processId)
            },
          },
        })
        return
      }

      try {
        const blob = await downloadPcgReportGenerationJobFile(job.jobId)
        downloadBlobAsFile(blob, resolvedFileName)
        completeProcess(tracked.processId, {
          title: 'PCG Report',
          message: 'Report generated. Your PDF download has started.',
        })
      } catch (error) {
        console.error('Failed to download completed PCG report job file', error)
        failProcess(tracked.processId, {
          title: 'PCG Report',
          message: 'Report generation completed, but downloading the PDF failed. Please try again.',
        })
      }
    },
    [completeProcess, failProcess, openPreviewFromJob]
  )

  const pollTrackedJob = useCallback(
    async (tracked: TrackedPcgReportJob) => {
      if (pollInFlightRef.current.has(tracked.jobId)) {
        return
      }

      pollInFlightRef.current.add(tracked.jobId)
      try {
        const job = await getPcgReportGenerationJob(tracked.jobId)
        updateProcess(tracked.processId, {
          title: 'PCG Report',
          message: buildPcgReportProgressToastMessage(job.progressPercent ?? 0, job.stage),
        })

        if (job.status === 'completed') {
          await handleCompletedJob(tracked, job)
          setTrackedJobs((current) => removeTrackedJob(current, tracked.jobId))
          return
        }

        if (job.status === 'failed' || job.status === 'expired') {
          failProcess(tracked.processId, {
            title: 'PCG Report',
            message: job.errorMessage?.trim() || 'Unable to generate the report right now.',
          })
          setTrackedJobs((current) => removeTrackedJob(current, tracked.jobId))
        }
      } catch (error) {
        console.error('Failed to poll PCG report generation job', error)
        failProcess(tracked.processId, {
          title: 'PCG Report',
          message: 'Unable to check report generation progress right now.',
        })
        setTrackedJobs((current) => removeTrackedJob(current, tracked.jobId))
      } finally {
        pollInFlightRef.current.delete(tracked.jobId)
      }
    },
    [failProcess, handleCompletedJob, updateProcess]
  )

  const trackedCount = Object.keys(trackedJobs).length
  useEffect(() => {
    if (trackedCount === 0) {
      return
    }

    const runPoll = () => {
      const snapshot = Object.values(trackedJobsRef.current)
      snapshot.forEach((tracked) => {
        void pollTrackedJob(tracked)
      })
    }

    runPoll()
    const timeoutId = window.setInterval(runPoll, POLL_INTERVAL_MS)
    return () => {
      window.clearInterval(timeoutId)
    }
  }, [pollTrackedJob, trackedCount])

  const ensureTrackingForReport = useCallback(
    async (reportId: string, options?: TrackPcgReportJobOptions) => {
      const normalizedReportId = String(reportId).trim()
      if (!normalizedReportId) {
        return
      }

      if (
        Object.values(trackedJobsRef.current).some(
          (tracked) => tracked.reportId === normalizedReportId
        )
      ) {
        return
      }

      if (ensureInFlightReportIdsRef.current.has(normalizedReportId)) {
        return
      }

      ensureInFlightReportIdsRef.current.add(normalizedReportId)
      try {
        const activeJob = await getActivePcgReportGenerationJob(normalizedReportId)
        if (!activeJob?.jobId) {
          return
        }

        trackPcgReportJob(activeJob, {
          fallbackFileName:
            options?.fallbackFileName?.trim() ||
            `pcg-report-${normalizedReportId}.pdf`,
        })
      } catch (error) {
        console.error('Failed to resume active PCG report generation job', error)
      } finally {
        ensureInFlightReportIdsRef.current.delete(normalizedReportId)
      }
    },
    [trackPcgReportJob]
  )

  const activeActionByReportId = useMemo(() => {
    const map: Record<string, PcgReportGenerationJobAction> = {}
    Object.values(trackedJobs).forEach((tracked) => {
      map[tracked.reportId] = tracked.action
    })
    return map
  }, [trackedJobs])

  const isReportJobActive = useCallback(
    (reportId?: string) => {
      if (!reportId) {
        return false
      }
      return Boolean(activeActionByReportId[String(reportId)])
    },
    [activeActionByReportId]
  )

  const getReportJobAction = useCallback(
    (reportId?: string) => {
      if (!reportId) {
        return null
      }
      return activeActionByReportId[String(reportId)] ?? null
    },
    [activeActionByReportId]
  )

  const value = useMemo<PcgReportGenerationTrackerContextValue>(
    () => ({
      trackPcgReportJob,
      ensureTrackingForReport,
      isReportJobActive,
      getReportJobAction,
    }),
    [ensureTrackingForReport, getReportJobAction, isReportJobActive, trackPcgReportJob]
  )

  return (
    <PcgReportGenerationTrackerContext.Provider value={value}>
      {children}
      {showPreviewModal ? (
        <React.Suspense fallback={null}>
          <PdfPreviewModal
            show={showPreviewModal}
            blob={previewBlob}
            title='PCG Report Preview'
            fileName={previewFileName}
            onClose={closePreviewModal}
          />
        </React.Suspense>
      ) : null}
    </PcgReportGenerationTrackerContext.Provider>
  )
}

export const usePcgReportGenerationTracker = () => {
  const context = useContext(PcgReportGenerationTrackerContext)
  if (!context) {
    throw new Error('usePcgReportGenerationTracker must be used within PcgReportGenerationTrackerProvider')
  }
  return context
}
